export const SECTION_NAME = "Estimate";

export const INTRO_TEXT = `An estimate is a quotation to a customer, or an
  offer to provide products or services at a specified price. The terms of an
  estimate (binding, non-binding, etc.) are set by your company policies.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "EST-12345",
    "date": "2019-05-08T09:13:00",
    "customer": {
      "id": 23,
      "name": "Test Customer"
    },
    "billing": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "shipping": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "salesRep": {
      "id": 12,
      "name": "Joe Smith"
    }
    "channel": {
      "id": 3,
      "name": "Catalog"
    },
    "department": {
      "id": 7,
      "name": "Packaged Goods"
    }
    "taxCode": {
      "id": 5,
      "name": "Sales Tax"
    },
    "currency": {
      "id": 42,
      "name": "USD"
    },
    "exchangeRate": 1.00000000,
    "customerMessage": "5 items to be shipped separately",
    "comment": "Pricing per unit",
    "customerNotes": "lead time test",
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Cust-Acct-No",
        "value": null,
        "dataType": "Number"
      }
    ],
    "depositPercent": 0,
    "depositAmount": 0,
    "subTotal": 12,
    "discountPercent": 0,
    "discountAmount": 0,
    "taxPercent": 8.25,
    "taxAmount": 0,
    "shippingAmount": 0,
    "total": 12,
    "discountTaxable": true,
    "shippingTaxable": true,
    "status": "pending",
    "acceptedBy": "Jimmy Wilkins"
    "acceptedDate": "2019-10-22T00:00:00",
    "expiration": "2019-10-24T00:00:00",
    "archived": false,
    "summaryOnly": false,
    "lastSync": "2019-10-22T00:00:00",
    "syncMessage": "",
    "hasSignature": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "class": null,
        "job": {
          "id": 51,
          "name": "A62"
        },
        "workcenter": {
          "id": 10,
          "name": "Punch"
        },
        "tax": {
          "taxable": true,
          "taxCode": {
            "id": 1,
            "name": "TAX"
          },
        },
        "description": null,
        "quantity": 1,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 12.00000,
        "amount": 12.00000,
        "cost": 1.00000,
        "margin": 1100.00000,
        "listprice": 0.00000,
        "percentdiscount": 0.00000
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a estimate, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "Customer for this transaction.",
  },
  {
    name: "createCustomerIfNotFound",
    type: "boolean",
    description:
      "If true, and the customer is not found, this option will create a new customer record. Only valid on POST.",
  },
  {
    name: "billing",
    type: "transaction address",
    description: `Billing address.`,
  },
  {
    name: "shipping",
    type: "transaction address",
    description: `Shipping address.`,
  },
  {
    name: "salesRep",
    type: "reference",
    description: `Sales representative for this transaction.`,
  },
  {
    name: "channel",
    type: "reference",
    description: `Channel (e.g., Catalog, Retail Store) for this transaction.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "taxCode",
    type: "reference",
    description: `Tax code for transaction.`,
  },
  {
    name: "currency",
    type: "reference",
    description: `Currency used for transaction, if multicurrency is enabled.`,
  },
  {
    name: "exchangeRate",
    type: "decimal",
    description: `The exchange rate used for this transaction, if multicurrency is enabled.`,
  },
  {
    name: "customerMessage",
    type: "string",
    description: `Customer message field.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "This comment is not visible to the customer on SOS-generated PDFs and emails, but the comment does appear on customer statements generated in QuickBooks.",
  },
  {
    name: "customerNotes",
    type: "string",
    description: `Field for internal notes about customer.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "depositPercent",
    type: "decimal",
    description: `Deposit percentage field.`,
  },
  {
    name: "depositAmount",
    type: "decimal",
    description: `Deposit amount field.`,
  },
  {
    name: "subTotal",
    type: "decimal",
    description: `Subtotal for transaction.`,
    readOnly: true,
  },
  {
    name: "discountPercent",
    type: "decimal",
    description: `Discount percentage field.`,
  },
  {
    name: "discountAmount",
    type: "decimal",
    description: `Discount amount field.`,
  },
  {
    name: "taxPercent",
    type: "decimal",
    description: `Tax percentage field.`,
  },
  {
    name: "taxAmount",
    type: "decimal",
    description: `Tax amount field.`,
  },
  {
    name: "shippingAmount",
    type: "decimal",
    description: `Shipping amount field.`,
  },
  {
    name: "total",
    type: "decimal",
    description: `Transaction total.`,
    readOnly: true,
  },
  {
    name: "discountTaxable",
    type: "boolean",
    description: `True if discount is taxable, false if not.`,
  },
  {
    name: "shippingTaxable",
    type: "boolean",
    description: `True if shipping is taxable, false if not.`,
  },
  {
    name: "status",
    type: "string",
    description: `The current status of the transaction. Options are "Accepted", "Pending", "Closed", or "Rejected".`,
  },
  {
    name: "acceptedBy",
    type: "string",
    description: "Name of customer representative who accepted this estimate.",
  },
  {
    name: "acceptedDate",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: "Date this estimate was accepted.",
  },
  {
    name: "expiration",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: "Date this estimate expires.",
  },
  {
    name: "closed",
    type: "boolean",
    description: `True if transaction is closed, false if not.`,
    readOnly: true,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `True if the summary parameter was set when this record was retrieved. False if not.`,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
  },
  {
    name: "statusLink",
    type: "string",
    description: `Link to OrderFacts page for this transaction, if enabled.`,
    readOnly: true,
  },
  {
    name: "lastSync",
    type: "date",
    newField: "New October 2nd",
    description: `The last successful sync time (GMT) for this estimate, if syncronizing with Quickbooks.`,
    readOnly: true,
  },
  {
    name: "syncMessage",
    type: "string",
    newField: "New October 2nd",
    description: `The sync error message if the last sync attempt failed with Quickbooks. This will be empty if synchronization is successful.`,
    readOnly: true,
  },
  {
    name: "lines",
    type: "array of sales line items",
    description: `The lines for the estimate.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this estimate line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the estimate transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "tax",
    type: "tax information object",
    description: `The tax information for this line, if enabled.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity of this item for this line.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitprice",
    type: "decimal",
    description: `The unit price for this item.  Must be above the item’s minimum price, if set.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: `The sales amount for this line item. The amount must equal the quantity multiplied by the unit price.`,
  },
  {
    name: "cost",
    type: "decimal",
    description: `The cost for this line.`,
  },
  {
    name: "margin",
    type: "decimal",
    description: `The margin for this line.`,
  },
  {
    name: "listprice",
    type: "decimal",
    description: `The list price for this item.`,
  },
  {
    name: "percentdiscount",
    type: "decimal",
    description: `The discount percentage applied to this line.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line item.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "status",
    type: "string: accepted/pending/closed/rejected",
    description: `This will filter the results to only those estimates with the specified status.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the estimate will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, comment, or customer name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "channel",
    type: "string",
    description: `Filters transactions according to the name of the channel.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/estimate?start=101&maxresults=100&status=open&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&location=warehouse&channel=catalog&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
